/* Barbados + Belville: Shopify's native discount is the authority for pricing. */
(function (root) {
  'use strict';
  function createSingleController(config, transport) {
    const buy = String(config.buy), gift = String(config.gift), marker = config.marker || 'barbados-belville-067';
    const base = config.root || '/';
    let queue = Promise.resolve();
    let pending = 0;
    const marked = item => String(item.variant_id) === gift && item.properties?._depot_gift === marker;
    const eligible = cart => cart.items.some(item => String(item.variant_id) === buy && item.quantity > 0 && !item.selling_plan_allocation);
    const enqueue = task => {
      pending++; config.busy?.(true);
      const operation = queue.then(task);
      queue = operation.catch(() => {});
      return operation.finally(() => { pending--; config.busy?.(pending > 0); });
    };
    async function json(url, options) {
      const response = await transport(url, { credentials: 'same-origin', cache: 'no-store', ...options });
      const value = await response.json();
      if (!response.ok || value.status >= 400) {
        const error = new Error('Cart request failed'); error.status = response.status; throw error;
      }
      return value;
    }
    const read = () => json(base + 'cart.js');
    async function change(item, quantity) {
      await json(base + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: item.key, quantity }) });
      return read();
    }
    async function sync() {
      let cart = await read();
      const qualifies = eligible(cart);
      const manual = cart.items.some(item => String(item.variant_id) === gift && !marked(item) && !item.selling_plan_allocation);
      // Delete by stable line key, re-read after every mutation (discounts can change keys).
      let removals = 0;
      while (cart.items.filter(marked).length > ((qualifies && !manual) ? 1 : 0)) {
        if (++removals > 100) throw new Error('Gift cleanup did not converge');
        cart = await change(cart.items.filter(marked).at(-1), 0);
      }
      let automatic = cart.items.find(marked);
      if (automatic && automatic.quantity !== 1) cart = await change(automatic, 1);
      let notice = '';
      if (qualifies && !manual && !cart.items.some(marked)) {
        const product = await json(base + 'products/' + (config.productHandle || '1-50-inch-belville-floating-shelves-2-shelves') + '.js');
        const variant = product.variants.find(item => String(item.id) === gift);
        if (variant?.available) {
          try {
            await json(base + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(gift), quantity: 1, properties: { _depot_gift: marker } }] }) });
          } catch (error) {
            if (error.status !== 422) throw error;
            notice = config.soldOut;
          }
          cart = await read();
        } else notice = config.soldOut;
      }
      // Never leave a chargeable item that we added as a promised gift.
      automatic = cart.items.find(marked);
      if (automatic && (!eligible(cart) || automatic.final_line_price !== 0)) {
        cart = await change(automatic, 0);
        notice = config.unavailable;
      }
      if (cart.items.some(item => marked(item) && item.final_line_price !== 0)) throw new Error('Unverified gift price');
      config.notice?.(notice);
      config.state?.(cart, eligible(cart));
      return cart;
    }
    async function section(cart, sectionId) {
      const target = new URL(config.page || base, config.origin || 'https://depoteshop.com');
      target.searchParams.set('sections', sectionId);
      const sections = await json(target.pathname + target.search);
      if (typeof sections[sectionId] !== 'string') throw new Error('Cart section unavailable');
      return { ...cart, sections };
    }
    return {
      sync: () => enqueue(sync),
      mutate: (url, options, sectionId) => enqueue(async () => {
        const response = await transport(url, options);
        const original = await response.clone().json();
        if (!response.ok || original.status >= 400) return response;
        try {
          const cart = await section(await sync(), sectionId);
          return new Response(JSON.stringify({ ...original, ...cart }), { status: 200, headers: { 'Content-Type': 'application/json' } });
        } catch (error) {
          config.notice?.(config.error);
          // The original operation may have succeeded. Do not retry its POST.
          throw error;
        }
      }),
      eligible,
      pending: () => pending
    };
  }
  // One serialized cart queue coordinates independent gift campaigns.
  function createController(config, transport) {
    if (!config.offers?.length) return createSingleController(config, transport);
    let queue = Promise.resolve(), pending = 0, notices = [];
    const base = config.root || '/';
    const children = config.offers.map(offer => createSingleController({
      ...config, ...offer, offers: undefined, busy: () => {}, state: () => {},
      notice: text => { if (text) notices.push(text); }
    }, transport));
    const eligible = cart => children.some(child => child.eligible(cart));
    const marked = item => config.offers.some(offer => String(item.variant_id) === String(offer.gift) && item.properties?._depot_gift === offer.marker);
    const enqueue = task => {
      pending++; config.busy?.(true);
      const operation = queue.then(task); queue = operation.catch(() => {});
      return operation.finally(() => { pending--; config.busy?.(pending > 0); });
    };
    async function json(url, options) {
      const response = await transport(url, { credentials: 'same-origin', cache: 'no-store', ...options });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error('Gift cart request failed');
      return data;
    }
    async function syncAll() {
      notices = [];
      for (const child of children) await child.sync();
      let cart = await json(base + 'cart.js');
      // A later discount may change an earlier gift's price: check the final cart.
      let removed = 0;
      while (cart.items.some(item => marked(item) && item.final_line_price !== 0)) {
        if (++removed > 100) throw new Error('Unverified final gift prices');
        const item = cart.items.find(item => marked(item) && item.final_line_price !== 0);
        await json(base + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: item.key, quantity: 0 }) });
        cart = await json(base + 'cart.js'); notices.push(config.unavailable);
      }
      config.notice?.([...new Set(notices)].join(' ')); config.state?.(cart, eligible(cart));
      return cart;
    }
    return {
      sync: () => enqueue(syncAll), eligible, pending: () => pending,
      mutate: (url, options, sectionId) => enqueue(async () => {
        const response = await transport(url, options);
        const original = await response.clone().json();
        if (!response.ok || original.status >= 400) return response;
        try {
          const cart = await syncAll();
          const target = new URL(config.page || base, config.origin || 'https://depoteshop.com'); target.searchParams.set('sections', sectionId);
          const sections = await json(target.pathname + target.search);
          if (typeof sections[sectionId] !== 'string') throw new Error('Cart section unavailable');
          return new Response(JSON.stringify({ ...original, ...cart, sections }), { status: 200, headers: { 'Content-Type': 'application/json' } });
        } catch (error) { config.notice?.(config.error); throw error; }
      })
    };
  }

  root.DepotGiftCore = { createController };
  if (!root.document || !root.DepotGiftConfig) return;
  const cfg = root.DepotGiftConfig, doc = root.document;
  const notice = text => {
    let box = doc.querySelector('[data-depot-gift-status]');
    if (!box) {
      box = doc.createElement('p'); box.dataset.depotGiftStatus = ''; box.setAttribute('role', 'status');
      box.setAttribute('aria-live', 'polite'); doc.body.appendChild(box);
    }
    box.textContent = text || ''; box.hidden = !text;
  };
  const controller = createController({ ...cfg,
    root: root.Shopify?.routes?.root || '/', origin: root.location.origin, page: root.location.pathname + root.location.search,
    busy: busy => doc.documentElement.classList.toggle('depot-gift-busy', busy),
    state: (cart, eligible) => doc.documentElement.classList.toggle('depot-gift-cart', eligible), notice
  }, root.fetch.bind(root));
  root.DepotGift = controller;
  function productState() {
    doc.querySelectorAll('[data-depot-gift-product]').forEach(box => {
      const form = box.closest('form');
      const input = form?.querySelector('[name="id"]');
      const selected = (cfg.offers || [cfg]).some(offer => String(input?.value) === String(offer.buy));
      const offer = box.querySelector('[data-depot-gift-offer]');
      if (offer) offer.hidden = !selected || offer.dataset.giftAvailable === 'false';
      const accelerated = box.querySelector('[data-depot-gift-accelerated]');
      if (accelerated) accelerated.hidden = selected;
    });
  }
  // Existing theme updates the variant input programmatically; observe only its value.
  const observer = new MutationObserver(productState);
  function bindProducts() {
    productState();
    doc.querySelectorAll('[data-depot-gift-product]').forEach(box => {
      const input = box.closest('form')?.querySelector('[name="id"]');
      if (input) observer.observe(input, { attributes: true, attributeFilter: ['value'] });
    });
  }
  doc.addEventListener('change', () => queueMicrotask(productState));
  doc.addEventListener('shopify:section:load', bindProducts);
  let approved = false;
  doc.addEventListener('submit', async event => {
    if (!event.submitter?.matches('[name="checkout"]') || approved) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const form = event.target, submitter = event.submitter;
    try {
      await controller.sync();
      approved = true;
      try { form.requestSubmit(submitter); } finally { approved = false; }
    } catch (_) { notice(cfg.error); }
  }, true);
  root.addEventListener('load', async () => {
    bindProducts();
    try {
      const before = await root.fetch((root.Shopify?.routes?.root || '/') + 'cart.js', { cache: 'no-store' }).then(r => r.json());
      if (controller.eligible(before) || before.items.some(i => (cfg.offers || [{marker:'barbados-belville-067'}]).some(o => i.properties?._depot_gift === o.marker))) {
        const cart = await controller.sync();
        if (JSON.stringify(before.items) !== JSON.stringify(cart.items)) {
          // Initial reconciliation updates the full cart page/drawer using native section rendering.
          const wrapper = doc.querySelector('[data-cart-wrapper]');
          const id = wrapper?.dataset.section || 'ajax-cart-drawer';
          const url = new URL(root.location.href); url.searchParams.set('sections', id);
          const sections = await root.fetch(url.pathname + url.search).then(r => r.json());
          if (typeof sections[id] === 'string' && typeof root.cartContentUpdate === 'function') root.cartContentUpdate({ ...cart, sections }, id);
        }
      }
    } catch (_) { notice(cfg.error); }
  });
})(globalThis);
